import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import Modal from 'src/components/Modal';
import useAuth from 'src/hooks/useAuth';
import useDeviceType from 'src/hooks/useDeviceType';
import usePortrait from 'src/hooks/usePortrait';
import useWindowSize from 'src/hooks/useWindowSize';
import ComposeMessageForm, { ComposeMessageFormProps } from './ComposeMessageForm';
import MessagePopUpContentWrapper from './ComposeMessagePopUpContentWrapper';

interface ComposeMessageModalProps extends ComposeMessageFormProps {
  visible: boolean;
  closeModal: () => void;
  isFormValid: boolean;
  isSendDisabled: boolean;
  onSend: () => Promise<void>;
  onCancel: () => void;
  statusMessage?: string;
  showStatus?: boolean;
}

const ComposeMessageModal: FC<ComposeMessageModalProps> = ({
  visible,
  closeModal,
  isFormValid,
  isSendDisabled,
  onSend,
  onCancel,
  errorState,
  replyToMessage,
  statusMessage,
  showStatus,
  ...formProps
}) => {
  const { t } = useTranslation();
  const { clientId, companyId, companyName, user } = useAuth();
  const { width } = useWindowSize();
  const deviceType = useDeviceType();
  const isPortrait = usePortrait();
  const sendButtonRef = useRef<HTMLButtonElement>(null);

  const modalWidth = useMemo(
    () =>
      deviceType !== 'desktop'
        ? isPortrait
          ? `${width}px`
          : `${Math.min(width, 740)}px`
        : '740px',
    [deviceType, isPortrait, width]
  );

  useEffect(() => {
    if (visible && sendButtonRef.current) {
      // Use setTimeout to ensure modal is fully rendered before focusing
      const timeoutId = window.setTimeout(() => {
        sendButtonRef.current?.focus();
      }, 100);
      return () => {
        window.clearTimeout(timeoutId);
      };
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      closeModal={closeModal}
      id="compose-message-modal"
      title={
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faEnvelope} className="text-white" />
          {replyToMessage ? t('Reply to Message') : t('Compose Message')}
        </div>
      }
      width={modalWidth}
      className="compose-message-modal-scrollable"
    >
      {/* Status Message */}
      {showStatus && statusMessage && (
        <div className="px-2 sm:px-6 py-2 border-b border-gray-200">
          <div
            className={`text-sm font-medium p-3 rounded ${
              statusMessage.includes(t('Success'))
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {statusMessage}
          </div>
        </div>
      )}

      {/* Modal Header with Buttons */}
      {!errorState.hasError && (
        <div className="flex flex-col items-center gap-1 px-2 sm:px-6 pt-2 pb-1">
          <div className="flex justify-center flex-wrap gap-3 w-full">
            <div className="relative flex flex-col items-center">
              <PopUp
                show={isSendDisabled && !isFormValid}
                className="z-50"
                content={
                  <div className="mt-2">
                    <MessagePopUpContentWrapper
                      type="Error"
                      contents={[
                        ...(formProps.selectedRecipients?.length === 0 &&
                        (!formProps.formData?.recipient ||
                          formProps.formData?.recipient.trim().length === 0)
                          ? [t('Recipient is required.')]
                          : []),
                        ...(!formProps.formData?.subject ||
                        formProps.formData?.subject.trim().length === 0
                          ? [t('Subject is required.')]
                          : []),
                        ...(!formProps.formData?.message ||
                        formProps.formData?.message.trim().length === 0
                          ? [t('Message body is required.')]
                          : []),
                      ]}
                      prefix=""
                    />
                  </div>
                }
              >
                <Button
                  color="primary"
                  onClick={onSend}
                  disabled={isSendDisabled}
                  ref={sendButtonRef}
                >
                  {t('Send Message')}
                </Button>
              </PopUp>
            </div>
            <Button color="secondary" onClick={onCancel}>
              {t('Cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* Modal Content */}
      <div className="px-2 sm:px-6 pt-3 pb-6">
        <ComposeMessageForm
          errorState={errorState}
          replyToMessage={replyToMessage}
          clientId={clientId}
          {...formProps}
        />
      </div>
    </Modal>
  );
};

export default ComposeMessageModal;
